import { DocumentReference } from 'firebase/firestore';
import React, { useMemo, useState } from 'react';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { deleteTemplate } from '../../../services/templates';
import { Template } from '../../../@types/domain';
import Popup from '../../../components/Popup';
import { logEvent, TrackingEvents } from '../../../tracking';
import ReactTooltip from 'react-tooltip';

import * as S from '../style';

function useDeleteTemplateMutation(
  mutationOptions: UseMutationOptions<void, Error, DocumentReference>
) {
  return useMutation<void, Error, DocumentReference>(
    ['templates', 'deleteTemplate'],
    (templateReference) => deleteTemplate(templateReference),
    mutationOptions
  );
}

function TemplateItem({
  template,
  isActive,
  onClick,
}: {
  template: Template;
  isActive: boolean;
  onClick: (template?: Template | undefined) => void;
}) {
  const [confirmDeletionPopupOpen, setConfirmDeletionPopupOpen] =
    useState<boolean>(false);
  const { mutateAsync: deleteTemplateMutation, isLoading } =
    useDeleteTemplateMutation({
      onSuccess() {
        onClick();
      },
    });
  const queryClient = useQueryClient();

  const handleTemplateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onClick(template);
  };
  const handleDeleteTemplateClick = (
    event: React.MouseEvent<HTMLOrSVGElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setConfirmDeletionPopupOpen(true);
  };
  const handleCancelDeletion = () => setConfirmDeletionPopupOpen(false);

  const handleConfirmDelete = () => {
    if (!template.ref) {
      throw new Error('Template reference not found');
    }

    if (!isLoading) {
      logEvent(TrackingEvents.DELETE_TEMPLATE);
      deleteTemplateMutation(template.ref).then(() =>
        queryClient.invalidateQueries(['templates'])
      );
    }
  };

  const dataForValue = useMemo(() => {
    return `template-item-${template.id}`;
  }, [template.id]);

  return (
    <>
      <S.TemplateItem
        data-tip
        data-for={dataForValue}
        isActive={isActive}
        onClick={handleTemplateClick}
      >
        <S.TemplateName>{template.name}</S.TemplateName>
        <S.DeleteTemplateIcon size={14} onClick={handleDeleteTemplateClick} />
      </S.TemplateItem>

      {confirmDeletionPopupOpen && (
        <Popup
          key={template.id}
          open
          title="Delete template"
          body={`Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`}
          cancelLabel="Cancel"
          onCancel={handleCancelDeletion}
          confirmLabel="Delete"
          onConfirm={handleConfirmDelete}
          disabledConfirm={isLoading}
        />
      )}
      <ReactTooltip
        id={dataForValue}
        place="right"
        effect="solid"
        border
        multiline
      >
        <S.TooltipContent>{template.name}</S.TooltipContent>
      </ReactTooltip>
    </>
  );
}

export default TemplateItem;
